import {createAsyncThunk} from '@reduxjs/toolkit'

import {ApiError, ApigwAuthErrors, HttpStatusCode, isApiError} from '@shared/helpers/api'

import {abortControllerManager} from './abortControllerManager'
import {getGwErrorMessage} from './error'
import {
    ApiAsyncThunkAuthError,
    ApiAsyncThunkFailError,
    ApiAsyncThunkServerError,
    ApiAsyncThunkTimeoutError,
    ApiAsyncThunkMaintenanceError,
    GwError,
    ApiAsyncThunkNetworkError,
    ApiAsyncThunkUnexpectedError,
    ApiAsyncThunkAbortError,
} from './interfaces/error'

import type {AsyncThunkPayloadCreator, Dispatch} from '@reduxjs/toolkit'

function isGwError(e: unknown): e is GwError {
    return !!(e && typeof e === 'object' && 'isGwError' in e && e['isGwError'] && 'errorData' in e && e['errorData'])
}

const parseServiceUnavailableError = (error: unknown) => {
    const gatewayError =
        !!error &&
        typeof error === 'string' &&
        getGwErrorMessage<{
            code: string
            message: string
            body: {code?: string; message?: string; additionalMessage?: string}
        }>(error)

    if (gatewayError && gatewayError.status === HttpStatusCode.ServiceUnavailable) {
        return gatewayError.data.result
    }

    return null
}

/**
 * API 비동기 Thunk 생성기
 * - 3번째 인자로 옵션을 추가할 수 있습니다.
 * - api 응답이 성공한경우는 그대로 반환합니다.
 * - api 응답이 실패한경우, rejectWithValue 로 감싸서 반환합니다.
 * - api 응답이 auth 로 실패한 경우, 커스텀 에러 객체를 rejectWithValue 로 감싸서 반환합니다.
 */
export function createApiAsyncThunk<
    Returned,
    ThunkArg = void,
    ThunkApiConfig extends {
        state?: unknown
        dispatch?: Dispatch
        extra?: unknown
        rejectValue?: unknown
        serializedErrorType?: unknown
        pendingMeta?: unknown
        fulfilledMeta?: unknown
        rejectedMeta?: unknown
    } = object,
>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkApiConfig>,
    {
        delay,
        beforeError,
        passAuth,
        rejectWithUnknownApiError,
        rejectWithUnknownError,
        cancelType,
    }: {
        delay?: number
        /**
         * error 처리전 가장 먼저 호출
         */
        beforeError?: (err: unknown) => void
        /**
         * 로그인되지 않았을때 정상 처리
         */
        passAuth?: () => Returned
        /**
         * api error 를 커스텀 객채로 반환
         */
        rejectWithUnknownApiError?: (err: ApiError) => object | undefined
        /**
         * 모든 조건 이후 에러를 처리할수 없을때, 커스텀 객체로 반환
         */
        rejectWithUnknownError?: (err: unknown) => object | undefined
        /**
         * 취소 처리 타입
         */
        cancelType?: 'previous' | 'none'
    } = {},
) {
    return createAsyncThunk<Returned, ThunkArg, ThunkApiConfig>(typePrefix, async (arg, thunkAPI) => {
        const cancelKey = typePrefix

        try {
            if (delay) {
                await new Promise((resolve) => setTimeout(resolve, delay))
            }

            if (cancelType === 'previous') {
                const combinedSignal = abortControllerManager.replaceSignal(cancelKey, thunkAPI.signal)
                const result = (await payloadCreator(arg, {...thunkAPI, signal: combinedSignal})) as Returned
                return result
            }

            const result = (await payloadCreator(arg, thunkAPI)) as Returned
            return result

            //  eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            beforeError && beforeError?.(e)

            //  eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rejectWithValue = thunkAPI.rejectWithValue as any

            if (rejectWithValue) {
                if (isGwError(e)) {
                    const result: ApiAsyncThunkAuthError | ApiAsyncThunkFailError = {
                        type: ApigwAuthErrors.includes(e.errorData.response?.data?.code)
                            ? 'API_ASYNC_THUNK_AUTH_ERROR'
                            : // 200 으로 내려왔어도, 00 이 아닌 에러인 경우 error 로 만들었을때 케이스가 대응 될 수 있음
                              // 추후 수정 필요
                              'API_ASYNC_THUNK_FAIL_ERROR',
                        actionType: typePrefix,
                        value: {
                            name: e.name,
                            response: e.errorData.response,
                        },
                    }
                    if (passAuth) {
                        return passAuth()
                    }
                    return rejectWithValue(result)
                }
                if (isApiError(e)) {
                    if (e.code === ApiError.ETIMEDOUT) {
                        const result: ApiAsyncThunkTimeoutError = {
                            type: 'API_ASYNC_THUNK_TIMEOUT_ERROR',
                            actionType: typePrefix,
                            value: {
                                code: e.code,
                                response: {
                                    status: e.response?.status,
                                    data: e.response?.data,
                                },
                            },
                        }
                        return rejectWithValue(result)
                    }
                    if (e.code === ApiError.ERR_NETWORK) {
                        const result: ApiAsyncThunkNetworkError = {
                            type: 'API_ASYNC_THUNK_NETWORK_ERROR',
                            actionType: typePrefix,
                            value: {
                                code: e.code,
                                response: {
                                    status: e.response?.status,
                                    data: e.response?.data,
                                },
                            },
                        }
                        return rejectWithValue(result)
                    }
                    // 503 케이스
                    if (e.response?.status === HttpStatusCode.ServiceUnavailable) {
                        const result: ApiAsyncThunkMaintenanceError = {
                            type: 'API_ASYNC_THUNK_MAINTENANCE_ERROR',
                            actionType: typePrefix,
                            value: {
                                // code: e.code,
                                response: {
                                    status: e.response.status,
                                    data: e.response.data,
                                    result: e.response.data.result,
                                },
                            },
                        }
                        return rejectWithValue(result)
                    }
                    /** abort로 인한 오류 추가 */
                    if (e.code === ApiError.ERR_CANCELED) {
                        const result: ApiAsyncThunkAbortError = {
                            type: 'API_ASYNC_THUNK_ABORT_ERROR',
                            actionType: typePrefix,
                            value: {
                                code: 'ABORTED_REQUEST',
                            },
                        }
                        return rejectWithValue(result)
                    }
                    if (rejectWithUnknownApiError) {
                        const result = rejectWithUnknownApiError(e)
                        if (result) {
                            return rejectWithValue(result)
                        }
                    }
                    // 500 또는 502
                    // 추후 400 등 추가 대응 필요
                    if (e.response) {
                        const result: ApiAsyncThunkServerError = {
                            type: 'API_ASYNC_THUNK_SERVER_ERROR',
                            actionType: typePrefix,
                            value: {
                                code: e.code,
                                response: {
                                    status: e.response.status,
                                    data: e.response.data,
                                    result: e.response.data?.result,
                                },
                            },
                        }
                        return rejectWithValue(result)
                    } else {
                        const result: ApiAsyncThunkUnexpectedError = {
                            type: 'API_ASYNC_THUNK_UNEXPECTED_ERROR',
                            actionType: typePrefix,
                            value: {
                                code: e.code,
                            },
                        }
                        return rejectWithValue(result)
                    }
                }

                if (e && typeof e === 'string') {
                    /**
                     * apigw에서 MaintenanceHandler Interceptor사용시 도달하는 영역
                     * @TODO axios helper에서 503에러 리턴하는 방법이 변경되면
                     * isApiError & e.response?.status === HttpStatusCode.ServiceUnavailable 로 검사하는 로직과 이 로직 중 하나로 통일하고 나머지 로직은 삭제 필요
                     */
                    // isMaintenanceError 에서 string 으로 변환
                    const parsedServiceUnavailableError = parseServiceUnavailableError(e)
                    if (parsedServiceUnavailableError) {
                        const result: ApiAsyncThunkMaintenanceError = {
                            type: 'API_ASYNC_THUNK_MAINTENANCE_ERROR',
                            actionType: typePrefix,
                            value: {
                                response: {
                                    status: HttpStatusCode.ServiceUnavailable,
                                    data: {
                                        code: '99',
                                        message: 'Service Unavailable',
                                        result: parsedServiceUnavailableError,
                                    },
                                    result: parsedServiceUnavailableError,
                                },
                            },
                        }
                        return rejectWithValue(result)
                    }
                }
                if (rejectWithUnknownError) {
                    const result = rejectWithUnknownError(e)
                    if (result) {
                        return rejectWithValue(result)
                    }
                }
            }
            throw e
        }
    })
}
